import { Box, Typography, Chip, Divider, Checkbox } from '@mui/material';
import { Info } from '@phosphor-icons/react';

interface StoreDetailsProps {
  store: any;
  brandsList: any;
}

const StoreDetail = ({ store, brandsList }: StoreDetailsProps) => {
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              width: '140px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
            }}
          >
            Outlet Alias
            <Info size={12} />
          </Typography>
          <Box
            sx={{
              height: 40,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderRadius: '6px',
              padding: '8px 12px',
              bgcolor: '#FFFFFF',
              border: '1px solid #DCE0E7',
              flex: 1,
            }}
          >
            <Typography sx={{ color: 'text.main', fontSize: '14px' }}>
              {store.friendly_name}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ width: '140px', fontSize: '14px' }}
          >
            Outlet Address
          </Typography>
          <Box
            sx={{
              height: 40,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderRadius: '6px',
              padding: '8px 12px',
              bgcolor: '#FFFFFF',
              border: '1px solid #DCE0E7',
              flex: 1,
            }}
          >
            <Typography sx={{ color: 'text.main', fontSize: '14px' }}>
              {store.street_address || 'N/A'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ width: '140px', fontSize: '14px' }}
          >
            Available Brands
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {store.brand_ids?.map((brandId: string) => {
              const brand = brandsList?.data?.data.find(
                (b: any) => b.id === brandId
              );
              return brand ? (
                <Chip
                  key={brand.id}
                  label={brand.name}
                  sx={{
                    bgcolor: '#E7F0FF',
                    borderRadius: '6px',
                    height: '32px',
                    color: 'text.main',
                    fontSize: '14px',
                    '& .MuiChip-label': {
                      px: 1.5,
                    },
                  }}
                />
              ) : null;
            })}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ width: '140px', fontSize: '14px' }}
          >
            Mode of Operation
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Checkbox
                checked={store.mode_of_operation?.includes('delivery')}
                disabled
                sx={{
                  color: '#7367F0',
                  width: 18,
                  height: 18,
                  borderRadius: '6px',
                  '& .MuiSvgIcon-root': {
                    width: 18,
                    height: 18,
                    borderRadius: '6px',
                  },
                  '&.Mui-checked': {
                    color: '#5A4CE6',
                  },
                  '&.Mui-disabled': {
                    color: store.mode_of_operation?.includes('delivery')
                      ? '#5A4CE6'
                      : '#6F6B7D40',
                  },
                }}
              />
              <Typography sx={{ color: 'text.main', fontSize: '14px' }}>
                Delivery
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Checkbox
                checked={store.mode_of_operation?.includes('take_away')}
                disabled
                sx={{
                  color: '#5A4CE6',
                  width: 18,
                  height: 18,
                  borderRadius: '6px',
                  '& .MuiSvgIcon-root': {
                    width: 18,
                    height: 18,
                    borderRadius: '6px',
                  },
                  '&.Mui-checked': {
                    color: '#5A4CE6',
                  },
                  '&.Mui-disabled': {
                    color: store.mode_of_operation?.includes('take_away')
                      ? '#5A4CE6'
                      : '#6F6B7D40',
                  },
                }}
              />
              <Typography sx={{ color: 'text.main', fontSize: '14px' }}>
                Takeaway
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            width: '100%',
          }}
        >
          <Typography
            variant="subtitle2"
            color="text.main"
            sx={{ fontSize: '14px', fontWeight: 500 }}
          >
            Outlet Contact Details
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: 'repeat(2, 1fr)',
              width: '100%',
            }}
          >
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontSize: '14px' }}
              >
                Name
              </Typography>
              <Box
                sx={{
                  bgcolor: '#FFFFFF',
                  border: '1px solid #DCE0E7',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography sx={{ color: 'text.main', fontSize: '14px' }}>
                  {store.poc_name}
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontSize: '14px' }}
              >
                Email
              </Typography>
              <Box
                sx={{
                  bgcolor: '#FFFFFF',
                  border: '1px solid #DCE0E7',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography sx={{ color: 'text.main', fontSize: '14px' }}>
                  {store.poc_email}
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontSize: '0.875rem' }}
              >
                Role
              </Typography>
              <Box
                sx={{
                  bgcolor: '#FFFFFF',
                  border: '1px solid #DCE0E7',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography sx={{ color: 'text.main', fontSize: '14px' }}>
                  {store.poc_role}
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontSize: '0.875rem' }}
              >
                Contact
              </Typography>
              <Box
                sx={{
                  bgcolor: '#FFFFFF',
                  border: '1px solid #DCE0E7',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography sx={{ color: 'text.main', fontSize: '14px' }}>
                  {store.poc_phone}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StoreDetail;
